import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  useRef,
  type ReactNode,
} from 'react';
import { ProjectStateContext } from './project-context';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  applyTrackerDomainSlug,
  readPromptRunIds,
  subscribePromptRunBatch,
  subscribePromptRunCleared,
  trackerAiClient as api,
} from '@/app/TrackerAi/api/trackerAiClient';
import {
  DUPLICATE_EXECUTION_MESSAGE,
  isDuplicateExecutionError,
} from '@/app/TrackerAi/utils/api-errors';
import {
  enqueueRunErrorMessage,
  schedulePromptRunRefresh,
} from '@/app/TrackerAi/utils/prompt-run';
import { useProjectData, type ProjectDataNeeds } from '../hooks/useProjectData';
import {
  applyFiltersToUrl,
  filtersUrlEquals,
  parseFiltersFromUrl,
} from '../utils/filter-url-sync';
import { MODEL_STORAGE_KEY } from '../utils/model-storage';
import { DEFAULT_VISIBILITY_RANGE } from '../utils/visibility-range';
import { trackerAiPath } from '../utils/paths';
import { clearProjectSetupDismissed } from '../utils/setup-onboarding-storage';
import type { AiModel, DomainFilterValue, Project, Topic, VisibilityRange } from '../types';
import type { NewPromptSubmitPayload } from '../components/Modals';

interface ProjectContextValue {
  projectId: string | null;
  project: Project | null;
  projects: Project[];
  projectsLoaded: boolean;
  projectsLoadFailed: boolean;
  projectsRetrying: boolean;
  reloadProjects: () => Promise<unknown>;
  setProjectId: (id: string) => void;
  topics: Topic[];
  /** False until the project topic list request settles. */
  topicsLoaded: boolean;
  userTopics: Topic[];
  topicFilterId: string | null;
  setTopicFilterId: (id: string | null) => void;
  visibilityRange: VisibilityRange;
  setVisibilityRange: (range: VisibilityRange) => void;
  customFrom: string;
  customTo: string;
  setCustomDateRange: (from: string, to: string) => void;
  modelFilterId: string | null;
  setModelFilterId: (id: string | null) => void;
  domainFilterId: DomainFilterValue;
  setDomainFilterId: (value: DomainFilterValue) => void;
  sourceFilterIds: string[];
  setSourceFilterIds: (ids: string[]) => void;
  toggleSourceFilterId: (id: string) => void;
  clearSourceFilterIds: () => void;
  models: AiModel[];
  defaultModelId: string;
  setDefaultModelId: (id: string) => void;
  data: ReturnType<typeof useProjectData>['data'];
  /** Date range resolved (stored / URL / auto) — range-based requests may start. */
  filtersReady: boolean;
  loading: boolean;
  error: string;
  setError: (msg: string) => void;
  loadFailed: boolean;
  refresh: (options?: { silent?: boolean }) => Promise<unknown>;
  /** Patch one prompt row after a run without full-page loading. */
  refreshPromptRow: (promptId: string) => Promise<unknown>;
  newPromptOpen: boolean;
  /** Topic to preselect in the create modal. Used when opening from a topic panel. */
  newPromptTopicId: string | null;
  setNewPromptOpen: (open: boolean, topicId?: string | null) => void;
  newTopicOpen: boolean;
  setNewTopicOpen: (open: boolean) => void;
  creatingPrompt: boolean;
  creatingTopic: boolean;
  /** Prompt IDs currently executing in the background after create/run. */
  runningPromptIds: string[];
  handleCreatePrompt: (payload: NewPromptSubmitPayload) => Promise<void>;
  handleCreateTopic: (payload: {
    name: string;
    language: string;
    marketCountry: string;
  }) => Promise<{ id: string; name: string }>;
  completeTopicSetup: () => Promise<void>;
  ensureProject: (body: {
    slug: string;
    domain: string;
    aliases?: string[];
  }) => Promise<{ id: string }>;
  ensuringProject: boolean;
  deleteProject: (id: string) => Promise<void>;
  deletingProject: boolean;
  setupOpenSignal: number;
  openProjectSetup: () => void;
}


/** Range-based lists each tab reads from `data`; other tabs skip those requests. */
function resolveProjectDataNeeds(pathname: string): ProjectDataNeeds {
  const section = pathname.replace(/^\/tracker-ai\/?/, '').split('/')[0];
  return {
    prompts: section === 'prompts',
    history: section === 'executions',
  };
}

export function ProjectProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const urlFiltersReady = useRef(false);
  const [projects, setProjects] = useState<Project[]>([]);
  const [projectsLoaded, setProjectsLoaded] = useState(false);
  const [projectsLoadFailed, setProjectsLoadFailed] = useState(false);
  const [projectsRetrying, setProjectsRetrying] = useState(false);
  const [projectId, setProjectIdState] = useState<string | null>(null);
  const [topicFilterId, setTopicFilterIdState] = useState<string | null>(null);
  const [visibilityRange, setVisibilityRangeState] = useState<VisibilityRange>(
    DEFAULT_VISIBILITY_RANGE,
  );
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [dateRangeSource, setDateRangeSource] = useState<'user' | 'auto'>('auto');
  const [dateFiltersReady, setDateFiltersReady] = useState(false);
  const [modelFilterId, setModelFilterIdState] = useState<string | null>(null);
  const [domainFilterId, setDomainFilterIdState] = useState<DomainFilterValue>('all');
  const [sourceFilterIds, setSourceFilterIdsState] = useState<string[]>([]);
  const [topics, setTopics] = useState<Topic[]>([]);
  const [topicsLoaded, setTopicsLoaded] = useState(false);
  const [models, setModels] = useState<AiModel[]>([]);
  const [defaultModelId, setDefaultModelIdState] = useState('');
  const [newPromptOpen, setNewPromptOpenState] = useState(false);
  const [newPromptTopicId, setNewPromptTopicId] = useState<string | null>(null);
  const [newTopicOpen, setNewTopicOpen] = useState(false);
  const [creatingPrompt, setCreatingPrompt] = useState(false);
  const [creatingTopic, setCreatingTopic] = useState(false);
  const [ensuringProject, setEnsuringProject] = useState(false);
  const [deletingProject, setDeletingProject] = useState(false);
  const [runningPromptIds, setRunningPromptIds] = useState<string[]>(() =>
    readPromptRunIds(),
  );
  const [setupOpenSignal, setSetupOpenSignal] = useState(0);
  const selectedDomain = useSelector(
    (state: { selectedDomain?: { name?: string; slug?: string } }) =>
      state.selectedDomain ?? {},
  );
  useEffect(() => {
    applyTrackerDomainSlug(selectedDomain.slug);
  }, [selectedDomain.slug]);

  useEffect(() => {
    const unsubStart = subscribePromptRunBatch((_batchId, ids) => {
      if (!ids.length) return;
      setRunningPromptIds((prev) => [...new Set([...prev, ...ids])]);
    });
    const unsubClear = subscribePromptRunCleared(() => setRunningPromptIds([]));
    return () => {
      unsubStart();
      unsubClear();
    };
  }, []);

  const projectDataFilters = useMemo(
    () => ({
      topicFilterId,
      visibilityRange,
      customFrom,
      customTo,
      modelFilterId,
      domainFilterId,
    }),
    [topicFilterId, visibilityRange, customFrom, customTo, modelFilterId, domainFilterId],
  );

  const projectDataNeeds = useMemo(() => resolveProjectDataNeeds(pathname), [pathname]);

  const {
    data,
    loading,
    error,
    setError,
    loadFailed,
    refresh,
    refreshPromptRow,
    refreshPromptsSilent,
  } = useProjectData(
    dateFiltersReady ? projectId : null,
    projectDataFilters,
    projectDataNeeds,
  );

  const project = useMemo(
    () => projects.find((p) => p.id === projectId) ?? null,
    [projects, projectId],
  );

  const userTopics = useMemo(
    () => topics.filter((t) => !t.isSystem),
    [topics],
  );

  const setProjectId = useCallback((id: string) => {
    setProjectIdState(id);
    const parsed = parseFiltersFromUrl(new URLSearchParams(window.location.search));
    setTopicFilterIdState(parsed.topicId);
    setModelFilterIdState(parsed.modelId);
    setDomainFilterIdState(parsed.domainFilterId ?? 'all');
    setSourceFilterIdsState(parsed.sourceFilterIds ?? []);
    setDateFiltersReady(false);
  }, []);

  const clearActiveProject = useCallback(() => {
    setProjectIdState(null);
    setTopicFilterIdState(null);
    setTopics([]);
    setDateFiltersReady(false);
  }, []);

  const resolveProjectForDomain = useCallback(
    (list: Project[], domainSlug?: string) => {
      const slug = domainSlug?.trim();
      if (!slug || slug === 'no-domain') return null;
      return list.find((p) => p.slug === slug) ?? null;
    },
    [],
  );

  const applyDomainProject = useCallback(
    (list: Project[], domainSlug?: string) => {
      const domainProject = resolveProjectForDomain(list, domainSlug);
      if (domainProject) {
        if (projectId !== domainProject.id) {
          setProjectId(domainProject.id);
        }
        return domainProject;
      }
      if (projectId !== null) clearActiveProject();
      return null;
    },
    [clearActiveProject, projectId, resolveProjectForDomain, setProjectId],
  );

  const setTopicFilterId = useCallback((id: string | null) => {
    setTopicFilterIdState(id);
  }, []);

  const setVisibilityRange = useCallback((range: VisibilityRange) => {
    setVisibilityRangeState(range);
    setDateRangeSource('user');
  }, []);

  const setCustomDateRange = useCallback((from: string, to: string) => {
    setCustomFrom(from);
    setCustomTo(to);
    setVisibilityRangeState('custom');
    setDateRangeSource('user');
  }, []);

  const setModelFilterId = useCallback((id: string | null) => {
    setModelFilterIdState(id);
  }, []);

  const setDomainFilterId = useCallback((value: DomainFilterValue) => {
    setDomainFilterIdState(value);
  }, []);

  const setSourceFilterIds = useCallback((ids: string[]) => {
    setSourceFilterIdsState(ids);
  }, []);

  const toggleSourceFilterId = useCallback((id: string) => {
    setSourceFilterIdsState((current) => {
      const next = current.includes(id)
        ? current.filter((item) => item !== id)
        : [...current, id];
      return next;
    });
  }, []);

  const clearSourceFilterIds = useCallback(() => {
    setSourceFilterIdsState([]);
  }, []);

  const setDefaultModelId = useCallback((id: string) => {
    setDefaultModelIdState(id);
    localStorage.setItem(MODEL_STORAGE_KEY, id);
    window.dispatchEvent(new CustomEvent('model-changed', { detail: id }));
  }, []);

  /** Re-read activity bounds after new executions (auto date mode only). */
  const syncAutoVisibilityWindow = useCallback(async () => {
    if (!projectId || dateRangeSource !== 'auto') return;
    try {
      const activity = await api.getProjectActivityRange(projectId);
      if (activity.suggestedFrom && activity.suggestedTo) {
        setVisibilityRangeState('custom');
        setCustomFrom(activity.suggestedFrom);
        setCustomTo(activity.suggestedTo);
      }
    } catch {
      /* keep current window */
    }
  }, [projectId, dateRangeSource]);

  const applyParsedNonDateUrlFilters = useCallback(
    (parsed: ReturnType<typeof parseFiltersFromUrl>) => {
      if (!parsed.hasAny) return;
      if (parsed.topicId !== undefined) setTopicFilterIdState(parsed.topicId);
      if (parsed.modelId !== undefined) setModelFilterIdState(parsed.modelId);
      if (parsed.domainFilterId !== undefined) setDomainFilterIdState(parsed.domainFilterId);
      if (parsed.sourceFilterIds !== undefined) setSourceFilterIdsState(parsed.sourceFilterIds);
    },
    [],
  );

  useEffect(() => {
    if (!projectId) {
      setDateFiltersReady(false);
      return;
    }

    let cancelled = false;
    setDateFiltersReady(false);

    async function resolveDateFilters(activeProjectId: string) {
      const parsed = parseFiltersFromUrl(searchParams);
      const urlIsExplicitPreference =
        parsed.range === 'custom'
          ? Boolean(parsed.from && parsed.to)
          : Boolean(parsed.range);

      if (urlIsExplicitPreference && parsed.range) {
        if (cancelled) return;
        setVisibilityRangeState(parsed.range);
        setCustomFrom(parsed.from ?? '');
        setCustomTo(parsed.to ?? '');
        setDateRangeSource('user');
        setDateFiltersReady(true);
        return;
      }

      try {
        const activity = await api.getProjectActivityRange(activeProjectId);
        if (cancelled) return;
        if (activity.suggestedFrom && activity.suggestedTo) {
          setVisibilityRangeState('custom');
          setCustomFrom(activity.suggestedFrom);
          setCustomTo(activity.suggestedTo);
        } else {
          setVisibilityRangeState(DEFAULT_VISIBILITY_RANGE);
          setCustomFrom('');
          setCustomTo('');
        }
        setDateRangeSource('auto');
      } catch {
        if (cancelled) return;
        setVisibilityRangeState(DEFAULT_VISIBILITY_RANGE);
        setCustomFrom('');
        setCustomTo('');
        setDateRangeSource('auto');
      } finally {
        if (!cancelled) setDateFiltersReady(true);
      }
    }

    void resolveDateFilters(projectId);
    return () => {
      cancelled = true;
    };
    // Resolve once per project; URL date params are read at switch time.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  useEffect(() => {
    if (!urlFiltersReady.current || !dateFiltersReady) return;
    const next = applyFiltersToUrl(searchParams, {
      visibilityRange,
      customFrom,
      customTo,
      topicFilterId,
      modelFilterId,
      domainFilterId,
      sourceFilterIds,
      syncDateRange: dateRangeSource === 'user',
    });
    if (!filtersUrlEquals(searchParams, next)) {
      setSearchParams(next, { replace: true });
    }
  }, [
    visibilityRange,
    customFrom,
    customTo,
    topicFilterId,
    modelFilterId,
    domainFilterId,
    sourceFilterIds,
    dateRangeSource,
    dateFiltersReady,
    searchParams,
    setSearchParams,
  ]);

  const reloadProjects = useCallback(() => {
    setProjectsRetrying(true);
    return api
      .getProjects()
      .then((list) => {
        setProjects(list);
        setProjectsLoadFailed(false);
        const parsed = parseFiltersFromUrl(searchParams);
        const matched = applyDomainProject(list, selectedDomain?.slug);
        if (matched) applyParsedNonDateUrlFilters(parsed);
      })
      .catch(() => {
        setProjectsLoadFailed(true);
      })
      .finally(() => {
        urlFiltersReady.current = true;
        setProjectsLoaded(true);
        setProjectsRetrying(false);
      });
  }, [applyDomainProject, applyParsedNonDateUrlFilters, selectedDomain?.slug]);

  useEffect(() => {
    void reloadProjects();
    // Only reload projects when domain changes — not on every URL param (e.g. ?prompt=).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applyDomainProject, applyParsedNonDateUrlFilters, selectedDomain?.slug]);

  useEffect(() => {
    if (projects.length === 0) return;
    applyDomainProject(projects, selectedDomain?.slug);
  }, [selectedDomain?.slug, projects, applyDomainProject]);

  useEffect(() => {
    if (!projectId) {
      setTopics([]);
      setTopicsLoaded(false);
      return;
    }
    let cancelled = false;
    api
      .getTopics(projectId)
      .then((list) => {
        if (!cancelled) setTopics(list);
      })
      .catch(() => {
        if (!cancelled) setTopics([]);
      })
      .finally(() => {
        if (!cancelled) setTopicsLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [projectId, data?.insight.counts.topics]);

  useEffect(() => {
    api
      .getPublicModels()
      .then((m) => {
        setModels(m);
        const stored = localStorage.getItem(MODEL_STORAGE_KEY);
        const initial =
          stored && m.some((x) => x.id === stored) ? stored : m[0]?.id ?? '';
        setDefaultModelIdState(initial);
      })
      .catch(() => undefined);
  }, []);

  const setNewPromptOpen = useCallback((open: boolean, topicId?: string | null) => {
    setNewPromptOpenState(open);
    setNewPromptTopicId(open ? topicId ?? null : null);
  }, []);

  const handleCreatePrompt = useCallback(
    async (payload: NewPromptSubmitPayload) => {
      if (!projectId || payload.texts.length === 0) return;
      setCreatingPrompt(true);
      setError('');
      try {
        const primaryModelId = payload.modelIds[0];
        const mode = payload.topicId ? 'structured' : 'quick';
        const createdPrompts: { id: string }[] = [];

        for (const text of payload.texts) {
          const created = await api.createProjectPrompt(projectId, {
            mode,
            text,
            modelId: primaryModelId,
            modelIds: payload.modelIds,
            topicId: payload.topicId,
          });
          createdPrompts.push(created);
        }

        const createdIds = createdPrompts.map((p) => p.id);
        setDefaultModelId(primaryModelId);
        setRunningPromptIds((prev) => [...new Set([...prev, ...createdIds])]);
        void refreshPromptsSilent();

        const runCreatedPrompts = async () => {
          const dropCreated = () =>
            setRunningPromptIds((prev) =>
              prev.filter((id) => !createdIds.includes(id)),
            );
          try {
            const result = await api.enqueuePromptRuns({ promptIds: createdIds });
            const enqueueError = enqueueRunErrorMessage(result);
            if (enqueueError) {
              setError(enqueueError);
            }
            if (result.createdItems === 0) dropCreated();
            schedulePromptRunRefresh(() => void refreshPromptsSilent());
            schedulePromptRunRefresh(() => void refresh({ silent: true }));
          } catch (e) {
            dropCreated();
            if (isDuplicateExecutionError(e)) {
              setError(DUPLICATE_EXECUTION_MESSAGE);
            } else {
              setError(e instanceof Error ? e.message : String(e));
            }
          } finally {
            await syncAutoVisibilityWindow();
            await refresh({ silent: true });
          }
        };

        // Default: run in background so the create modal can close immediately.
        // Setup onboarding passes awaitRuns to keep the tracking UI until /run finishes.
        if (payload.awaitRuns) {
          await runCreatedPrompts();
        } else {
          void runCreatedPrompts();
        }

        if (payload.navigateAfterCreate !== true) return;
        const lastCreated = createdPrompts[createdPrompts.length - 1];
        if (lastCreated) {
          navigate(
            trackerAiPath(
              'prompts',
              selectedDomain?.slug,
              selectedDomain?.name,
              `prompt=${lastCreated.id}`,
            ),
          );
        }
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
        throw e;
      } finally {
        setCreatingPrompt(false);
      }
    },
    [projectId, refresh, refreshPromptRow, refreshPromptsSilent, navigate, setDefaultModelId, setError, syncAutoVisibilityWindow, selectedDomain?.slug, selectedDomain?.name],
  );

  const ensureProject = useCallback(
    async (body: {
      slug: string;
      domain: string;
      aliases?: string[];
    }) => {
      setEnsuringProject(true);
      setError('');
      try {
        const ensured = await api.ensureProject(body);
        const list = await api.getProjects();
        setProjects(list);
        setProjectId(ensured.id);
        return ensured;
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
        throw e;
      } finally {
        setEnsuringProject(false);
      }
    },
    [setProjectId, setError],
  );

  const deleteProject = useCallback(
    async (id: string) => {
      setDeletingProject(true);
      setError('');
      try {
        await api.deleteProject(id);
        const list = await api.getProjects();
        setProjects(list);
        if (projectId === id) {
          const domainProject = resolveProjectForDomain(list, selectedDomain?.slug);
          if (domainProject) {
            setProjectId(domainProject.id);
          } else {
            clearActiveProject();
            navigate(
              trackerAiPath('overview', selectedDomain?.slug, selectedDomain?.name),
            );
          }
        }
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
        throw e;
      } finally {
        setDeletingProject(false);
      }
    },
    [projectId, setProjectId, clearActiveProject, resolveProjectForDomain, selectedDomain?.slug, selectedDomain?.name, navigate, setError],
  );

  const handleCreateTopic = useCallback(
    async (payload: {
      name: string;
      language: string;
      marketCountry: string;
    }): Promise<{ id: string; name: string }> => {
      if (!projectId || !payload.name.trim()) {
        throw new Error('پروژه و نام تاپیک الزامی است');
      }
      setCreatingTopic(true);
      setError('');
      try {
        const topic = await api.createTopic(
          projectId,
          payload.name.trim(),
          payload.language,
          undefined,
          payload.marketCountry,
        );
        const list = await api.getTopics(projectId);
        setTopics(list);
        return topic;
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
        throw e;
      } finally {
        setCreatingTopic(false);
      }
    },
    [projectId, setError],
  );

  const completeTopicSetup = useCallback(async () => {
    await refresh();
  }, [refresh]);

  const openProjectSetup = useCallback(() => {
    if (projectId) clearProjectSetupDismissed(projectId);
    setSetupOpenSignal((signal) => signal + 1);
    const slug = selectedDomain?.slug;
    const name = selectedDomain?.name;
    if (!slug || !name || slug === 'no-domain') return;
    navigate(trackerAiPath('onboarding', slug, name));
  }, [projectId, selectedDomain?.slug, selectedDomain?.name, navigate]);

  const value: ProjectContextValue = {
    projectId,
    project,
    projects,
    projectsLoaded,
    projectsLoadFailed,
    projectsRetrying,
    reloadProjects,
    setProjectId,
    topics,
    topicsLoaded,
    userTopics,
    topicFilterId,
    setTopicFilterId,
    visibilityRange,
    setVisibilityRange,
    customFrom,
    customTo,
    setCustomDateRange,
    modelFilterId,
    setModelFilterId,
    domainFilterId,
    setDomainFilterId,
    sourceFilterIds,
    setSourceFilterIds,
    toggleSourceFilterId,
    clearSourceFilterIds,
    models,
    defaultModelId,
    setDefaultModelId,
    data,
    filtersReady: dateFiltersReady,
    loading: !dateFiltersReady || loading,
    error,
    setError,
    loadFailed,
    refresh,
    refreshPromptRow,
    newPromptOpen,
    newPromptTopicId,
    setNewPromptOpen,
    newTopicOpen,
    setNewTopicOpen,
    creatingPrompt,
    creatingTopic,
    runningPromptIds,
    handleCreatePrompt,
    handleCreateTopic,
    completeTopicSetup,
    ensureProject,
    ensuringProject,
    deleteProject,
    deletingProject,
    setupOpenSignal,
    openProjectSetup,
  };

  return (
    <ProjectStateContext.Provider value={value}>{children}</ProjectStateContext.Provider>
  );
}

export function useProject() {
  const ctx = useContext(ProjectStateContext) as ProjectContextValue | null;
  if (!ctx) throw new Error('useProject must be used within ProjectProvider');
  return ctx;
}
